import React, { useEffect, useId, useState } from "react";
import Link from "@/shared/lib/router/link";
import { useRouter } from "@/shared/lib/router/router";
import { ChevronRight } from "lucide-react";
import { SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from "@/shared/ui/kit/sidebar";
import { useInventoryPermissions } from "@/features/inventory/hooks/useInventoryPermissions";
import { INVENTORY_SUBMENU_SECTIONS } from "@/features/inventory/constants/inventoryTabs";
import { cn } from "@/shared/lib/cn";

function InventorySection({ title, isActive, children }: { title: string; isActive: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(isActive);
  const contentId = useId();

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  return (
    <SidebarMenuSubItem>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex h-8 w-full items-center justify-between rounded-md px-2 text-[11px] font-bold uppercase tracking-wide outline-none transition-colors focus-visible:ring-2 focus-visible:ring-nav_accent",
          isActive ? "text-nav_accent" : "text-white/60 hover:text-white"
        )}
      >
        <span>{title}</span>
        <ChevronRight size={14} className={cn("transition-transform duration-base", open && "rotate-90")} />
      </button>
      {open && (
        <SidebarMenuSub id={contentId} className="ml-2">
          {children}
        </SidebarMenuSub>
      )}
    </SidebarMenuSubItem>
  );
}

export default function InventorySidebarMenu() {
  const router = useRouter();
  const flags = useInventoryPermissions();
  const { setOpenMobile } = useSidebar();
  const area = router.query.area as string | undefined;
  const subTab = router.query.subTab as string | undefined;
  const closeMobile = () => setOpenMobile(false);

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={router.pathname === "/inventory"}>
          <Link href="/inventory" onClick={closeMobile}>
            <span>Início</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>

      {INVENTORY_SUBMENU_SECTIONS.map((section) => {
        const permittedItems = section.items.filter((item) => {
          if (item.flag === "ALWAYS") return true;
          if (Array.isArray(item.flag)) return item.flag.some((f) => flags[f as keyof typeof flags]);
          return flags[item.flag as keyof typeof flags];
        });

        if (permittedItems.length === 0) return null;

        return (
          <InventorySection key={section.id} title={section.title} isActive={area === section.id}>
            {permittedItems.map((item) => (
              <SidebarMenuSubItem key={item.key}>
                <SidebarMenuSubButton asChild isActive={area === section.id && subTab === item.key}>
                  <Link href={`/inventory/${section.id}?subTab=${item.key}`} onClick={closeMobile}>
                    <span>{item.label}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </InventorySection>
        );
      })}
    </SidebarMenuSub>
  );
}
